/**
 * RectShape - Konva rectangle with field label
 * Uses Rect directly as the transformable element for reliable Transformer behavior
 */
import { useRef, useEffect, useMemo } from 'react';
import { Rect, Text } from 'react-konva';
import Konva from 'konva';
import type { TemplateShape, CustomizationField } from '@jigflow/shared';
import { CursorMode } from '../useTemplateStore';

/**
 * Calculate the largest font size that fits text within a bounding box.
 * Uses a temporary Konva.Text node for real measurement — no heuristics.
 */
function calculateFitFontSize(text: string, maxWidth: number, maxHeight: number): number {
    const refSize = 100;
    const temp = new Konva.Text({ text, fontSize: refSize });
    const naturalWidth = temp.width();
    const naturalHeight = temp.height();
    temp.destroy();

    if (naturalWidth <= 0 || naturalHeight <= 0) return Math.min(maxWidth, maxHeight);

    const scale = Math.min(maxWidth / naturalWidth, maxHeight / naturalHeight);
    return Math.max(1, refSize * scale);
}

interface RectShapeProps {
    shape: TemplateShape;
    field?: CustomizationField;
    isSelected: boolean;
    isLocked?: boolean;
    workAreaWidth: number;
    workAreaHeight: number;
    scale: number;
    cursorMode?: CursorMode;
    onSelect: () => void;
    onChange: (updates: Partial<TemplateShape>) => void;
    onDragMove?: (id: string, x: number, y: number, width: number, height: number, node: Konva.Node) => void;
    onDragEnd?: () => void;
    registerRef: (id: string, node: Konva.Rect | null) => void;
}

export function RectShape({
    shape,
    field,
    isSelected,
    isLocked = false,
    workAreaWidth: _workAreaWidth,
    workAreaHeight: _workAreaHeight,
    scale,
    cursorMode = 'move',
    onSelect,
    onChange,
    onDragMove,
    onDragEnd: onDragEndCallback,
    registerRef
}: RectShapeProps) {
    void _workAreaWidth;
    void _workAreaHeight;
    const rectRef = useRef<Konva.Rect>(null);

    useEffect(() => {
        if (!isLocked) {
            registerRef(shape.id, rectRef.current);
        }
        return () => registerRef(shape.id, null);
    }, [shape.id, registerRef, isLocked]);

    const isAssigned = !!shape.fieldId;
    const canDrag = cursorMode === 'move' && !isLocked && isSelected;

    const engraveOn = shape.engrave !== false;
    const scoreOn = shape.score === true;
    const cutOn = shape.cut === true;
    const hasAnyLaserOp = isAssigned && (scoreOn || engraveOn || cutOn);
    const allOpsOff = isAssigned && shape.engrave === false && !scoreOn && !cutOn;

    let fillColor: string;
    let strokeColor: string;
    let strokeWidth: number;
    let dashArray: number[] | undefined;

    if (isLocked) {
        fillColor = 'transparent';
        strokeColor = '#000000';
        strokeWidth = 1 / scale;
        dashArray = undefined;
    } else if (hasAnyLaserOp || allOpsOff) {
        fillColor = 'transparent';
        strokeColor = '#cccccc';
        dashArray = [4 / scale, 4 / scale];
        strokeWidth = 1 / scale;
    } else if (isAssigned) {
        fillColor = 'rgba(34, 197, 94, 0.1)';
        strokeColor = isSelected ? '#3b82f6' : '#22c55e';
        strokeWidth = (isSelected ? 2 : 1) / scale;
        dashArray = undefined;
    } else {
        fillColor = 'rgba(59, 130, 246, 0.1)';
        strokeColor = isSelected ? '#3b82f6' : '#8b5cf6';
        strokeWidth = (isSelected ? 2 : 1) / scale;
        dashArray = [5 / scale, 5 / scale];
    }

    let textFillColor: string | undefined = '#22c55e';
    let textStrokeColor: string | undefined = undefined;
    let textStrokeWidth = 0;

    if (allOpsOff) {
        textFillColor = 'rgba(180, 180, 180, 0.5)';
        textStrokeColor = 'rgba(150, 150, 150, 0.6)';
        textStrokeWidth = 0.5;
    } else if (hasAnyLaserOp) {
        if (engraveOn) {
            textFillColor = shape.engraveColor || '#000000';
        } else {
            textFillColor = 'transparent';
        }

        if (scoreOn) {
            textStrokeColor = shape.scoreColor || '#fe0002';
            textStrokeWidth = 0.15;
        } else if (cutOn) {
            textStrokeColor = shape.cutColor || '#00befe';
            textStrokeWidth = 0.15;
        } else {
            textStrokeColor = undefined;
            textStrokeWidth = 0;
        }
    }

    const handleDragMoveInternal = (e: Konva.KonvaEventObject<DragEvent>) => {
        const node = e.target;
        if (onDragMove) {
            onDragMove(shape.id, node.x(), node.y(), shape.width, shape.height, node);
        }
    };

    const handleDragEnd = (e: Konva.KonvaEventObject<DragEvent>) => {
        const node = e.target;
        onChange({
            x: node.x(),
            y: node.y()
        });
        if (onDragEndCallback) {
            onDragEndCallback();
        }
    };

    const handleTransformEnd = (e: Konva.KonvaEventObject<Event>) => {
        const node = e.target as Konva.Rect;
        const scaleX = node.scaleX();
        const scaleY = node.scaleY();

        const newWidth = Math.max(5, node.width() * scaleX);
        const newHeight = Math.max(5, node.height() * scaleY);

        node.scaleX(1);
        node.scaleY(1);
        node.width(newWidth);
        node.height(newHeight);

        onChange({
            x: node.x(),
            y: node.y(),
            width: newWidth,
            height: newHeight,
            rotation: node.rotation()
        });
    };

    const textX = shape.x;
    const textY = shape.y;

    // Measure-based font size: always fits text within the rect
    const fitFontSize = useMemo(
        () => field ? calculateFitFontSize(field.label, shape.width, shape.height) : 0,
        [field, shape.width, shape.height]
    );

    return (
        <>
            <Rect
                ref={rectRef}
                x={shape.x}
                y={shape.y}
                width={shape.width}
                height={shape.height}
                rotation={shape.rotation || 0}
                fill={fillColor}
                stroke={strokeColor}
                strokeWidth={strokeWidth}
                dash={dashArray}
                draggable={canDrag}
                onClick={onSelect}
                onTap={onSelect}
                onDragMove={canDrag ? handleDragMoveInternal : undefined}
                onDragEnd={canDrag ? handleDragEnd : undefined}
                onTransformEnd={canDrag ? handleTransformEnd : undefined}
            />

            {field && !isLocked && (
                <Text
                    text={field.label}
                    x={textX}
                    y={textY}
                    width={shape.width}
                    height={shape.height}
                    rotation={shape.rotation || 0}
                    align="center"
                    verticalAlign="middle"
                    fontSize={fitFontSize}
                    fill={textFillColor}
                    stroke={textStrokeColor}
                    strokeWidth={textStrokeWidth}
                    listening={false}
                />
            )}

            {!isAssigned && !isLocked && (
                <Text
                    text="?"
                    x={textX}
                    y={textY}
                    width={shape.width}
                    height={shape.height}
                    rotation={shape.rotation || 0}
                    align="center"
                    verticalAlign="middle"
                    fontSize={Math.min(shape.height * 0.9, shape.width * 0.9)}
                    fill="#8b5cf6"
                    listening={false}
                />
            )}
        </>
    );
}
